import {
  ArrowRightIcon,
  Button,
  CaretRightIcon,
  PencilSimpleIcon,
  ProgressBar,
  UserGroupIcon,
} from "@stacklet/ui";
import { generatePath } from "react-router";

import { Link, TableLink } from "app/components/links";
import { IdentityProviderLogo } from "app/components/rbac/IdentityProviderLogo";
import { paths } from "app/constants/paths";

import { type GroupBand, type GroupRow } from "./groupBands";

/** A band's leading glyph: the provider logo, or a users icon for local groups. */
function BandLogo({ idpType }: { idpType?: string | null }) {
  return (
    <div className="flex shrink-0 items-center justify-center rounded-full bg-overlay-medium p-xs">
      {idpType ? (
        <IdentityProviderLogo idpType={idpType} size={14} />
      ) : (
        <UserGroupIcon className="text-text-secondary" size={14} />
      )}
    </div>
  );
}

interface BandCellProps {
  /** The band the row represents. */
  band: GroupBand;
  /** Whether the band's groups are loading (shows a thin bar at the base). */
  isLoading: boolean;
}

/** The full-width band header: logo, name, and its row actions. */
export function BandCell({ band, isLoading }: BandCellProps) {
  return (
    <div className="relative flex h-full w-full items-center justify-between">
      {isLoading ? (
        <div className="absolute inset-x-0 bottom-0">
          <ProgressBar size="slim" indeterminate />
        </div>
      ) : null}
      <div className="flex items-center gap-sm">
        <BandLogo idpType={band.idpType} />
        <span className="text-label-medium-strong text-text-primary">
          {band.label}
        </span>
        {band.secondary ? (
          <span className="text-label-small text-text-secondary">
            {band.secondary}
          </span>
        ) : null}
      </div>
      {/* Same gap as the group rows' actions column (the buttons' own p-md
          gives the hit area and right-edge spacing), so the band's edit/arrow
          line up with the group actions below it. */}
      <div className="flex items-center gap-xxs">
        {band.editTo ? (
          <Link
            className="inline-flex p-md text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={band.editTo}
          >
            <PencilSimpleIcon aria-label={`Edit ${band.label}`} size={16} />
          </Link>
        ) : null}
        {band.to ? (
          <Link
            className="inline-flex p-md text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={band.to}
          >
            <ArrowRightIcon aria-label={`View ${band.label}`} size={16} />
          </Link>
        ) : null}
      </div>
    </div>
  );
}

interface GroupNameCellProps {
  /** The band the group belongs to (for its logo and "Synced from" line). */
  band: GroupBand;
  /** The group shown in the cell. */
  group: GroupRow;
  /** Whether the group's panel is open. */
  isExpanded: boolean;
  /** Toggles the group's panel open/closed. */
  onToggle: () => void;
}

/** A group row's leading cell: expand toggle, logo, linked name, origin line. */
export function GroupNameCell({
  band,
  group,
  isExpanded,
  onToggle,
}: GroupNameCellProps) {
  return (
    <div className="flex h-full min-w-0 items-center gap-sm">
      <Button
        IconLeft={
          <CaretRightIcon
            className={isExpanded ? "rotate-90" : undefined}
            size={16}
          />
        }
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.label}`}
        className="shrink-0 text-text-secondary hover:text-text-primary"
        onPress={onToggle}
        type="button"
        variant="plain"
      />
      <BandLogo idpType={band.idpType} />
      <div className="leading-tight min-w-0">
        <TableLink
          name={group.label}
          to={generatePath(paths.groupDetails, { groupUuid: group.uuid })}
        />
        <div className="truncate text-label-small text-text-secondary">
          {band.idpDisplayName ? `Synced from ${band.idpDisplayName}` : "Local"}
        </div>
      </div>
    </div>
  );
}
